import React from 'react';
import { Alert, Box, Button, Chip, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddIcon from '@mui/icons-material/Add';
import { StargraveActiveBuff, StargraveBuff, StargraveCrewData, StargraveLeader, StargraveRewireStat, StargraveSelectedPower } from '../../types';
import { stargraveBackgrounds, stargravePowers } from '../../data/stargrave/coreRules';
import { getAllStargraveSoldiers } from '../../stores/stargraveCustomStore';
import { PowerCard } from '../shared/PowerCard';
import { getStargraveBuffForPower } from '../../game/stargraveBuffRules';
import {
  getStargraveUpkeepSuits,
  getStargraveUpkeepWaiverLimit,
  getStargraveUpkeepCost,
  getArmouryBoostableWeapons,
  getShipUpgradePowerHints,
  isFieldedStargraveSoldier,
  StargraveArmourUpkeepOptions,
  StargraveArmouryWeaponBoost,
  StargraveUpkeepDecision,
  StargraveUpkeepReplacement,
} from '../../game/stargraveUpkeepRules';

interface StargravePreGameTabProps {
  crewData: StargraveCrewData;
  availableCredits: number;
  onAddTemporarySoldier: () => void;
  onRemoveSoldier: (soldierId: string) => void;
  onStartGame: (options: {
    lingeringPayments: Record<string, boolean>;
    badlyWoundedPayments: Record<string, boolean>;
    armourUpkeep: StargraveArmourUpkeepOptions;
    weaponLocker?: { soldierId: string };
    powerBuffs?: Array<{ buffId: string; memberId: string; strainDamage?: number }>;
    robotRewires?: Array<{ soldierId: string; stat: StargraveRewireStat }>;
  }) => void;
}

interface PreGamePower {
  key: string;
  leader: StargraveLeader;
  power: typeof stargravePowers[number];
  selection: StargraveSelectedPower;
}

export const StargravePreGameTab: React.FC<StargravePreGameTabProps> = ({ crewData, availableCredits, onAddTemporarySoldier, onRemoveSoldier, onStartGame }) => {
  const leaders = [crewData.captain, crewData.firstMate].filter(
    (leader): leader is StargraveLeader => Boolean(leader)
  );
  const powers: PreGamePower[] = leaders.flatMap((leader) => leader.powers.flatMap((selection) => {
    const power = stargravePowers.find((candidate) => candidate.id === selection.powerId);
    // Out of Game (A) powers are resolved in post-game instead.
    if (!power || !power.timing.includes('Out of Game (B)')) return [];
    return [{ key: `${leader.id}:${power.id}`, leader, power, selection }];
  }));
  const [selectedPowers, setSelectedPowers] = React.useState<string[]>([]);
  const [lingeringPayments, setLingeringPayments] = React.useState<Record<string, boolean>>({});
  const [badlyWoundedPayments, setBadlyWoundedPayments] = React.useState<Record<string, boolean>>({});
  const [upkeepDecisions, setUpkeepDecisions] = React.useState<Record<string, StargraveUpkeepDecision>>({});
  const [upkeepReplacements, setUpkeepReplacements] = React.useState<Record<string, StargraveUpkeepReplacement | 'none'>>({});
  const [weaponBoosts, setWeaponBoosts] = React.useState<StargraveArmouryWeaponBoost[]>([]);
  const [weaponLockerSoldierId, setWeaponLockerSoldierId] = React.useState('');
  const [powerBuffTargets, setPowerBuffTargets] = React.useState<Record<string, string>>({});
  const [rewireSelections, setRewireSelections] = React.useState<Record<string, { soldierId: string; stat: StargraveRewireStat }>>({});

  const upkeepSuits = getStargraveUpkeepSuits(crewData);
  const waiverLimit = getStargraveUpkeepWaiverLimit(crewData);
  const armouryResolved = selectedPowers.some((key) => key.endsWith(':armoury'));
  const hasArmouryPower = powers.some(({ power }) => power.id === 'armoury');
  // Waivers and boosts only stand while the Armoury power is marked resolved.
  const effectiveBoosts = armouryResolved ? weaponBoosts : [];
  const effectiveDecisions: Record<string, StargraveUpkeepDecision> = Object.fromEntries(
    upkeepSuits.map((suit) => {
      const decision = upkeepDecisions[suit.key] ?? 'pay';
      return [suit.key, decision === 'waived' && !armouryResolved ? 'pay' : decision];
    })
  );
  const waivedCount = upkeepSuits.filter((suit) => effectiveDecisions[suit.key] === 'waived').length;
  const armouryUsesSpent = waivedCount + effectiveBoosts.length;
  const totalUpkeepCost = getStargraveUpkeepCost(upkeepSuits, effectiveDecisions);
  // Members benched by an unpaid soldier suit can't receive a boost either.
  const benchedSoldierIds = new Set(upkeepSuits
    .filter((suit) => suit.key.startsWith('soldier:') && effectiveDecisions[suit.key] === 'unpaid')
    .map((suit) => suit.ownerId));
  const memberActiveBuffs = new Map<string, StargraveActiveBuff[]>([
    ...leaders.map((leader) => [leader.id, leader.gameState.activeBuffs] as const),
    ...crewData.soldiers.map((soldier) => [soldier.id, soldier.gameState.activeBuffs] as const),
  ]);
  const alreadyHasBuff = (memberId: string, buffId: string, stacking = 1) => {
    const existing = memberActiveBuffs.get(memberId)?.find((active) => active.buffId === buffId);
    return Boolean(existing && existing.stackCount >= stacking);
  };
  const boostOptions = getArmouryBoostableWeapons(crewData)
    .filter((option) => !benchedSoldierIds.has(option.memberId) && option.memberId !== weaponLockerSoldierId
      && !alreadyHasBuff(option.memberId, 'armoury-weapon-boost'));
  const hasWeaponLocker = crewData.ship.upgradeIds.includes('weapon-locker');
  const weaponLockerCandidates = crewData.soldiers.filter((soldier) =>
    isFieldedStargraveSoldier(soldier) && !soldier.isTemporary && !benchedSoldierIds.has(soldier.id) &&
    !weaponBoosts.some((boost) => boost.memberId === soldier.id) &&
    !alreadyHasBuff(soldier.id, 'weapon-locker-boost'));
  // Combat-armour wearers are the upkeep suit owners (Armour Plates cannot target them).
  const combatArmourWearerIds = new Set(upkeepSuits.map((suit) => suit.ownerId));
  const buffTargetOptions = (power: PreGamePower['power'], leader: StargraveLeader, buff: StargraveBuff) => {
    const options = (power.timing.includes('Self Only')
      ? [{ id: leader.id, name: leader.name }]
      : [
          ...leaders.map((candidate) => ({ id: candidate.id, name: candidate.name })),
          ...crewData.soldiers
            .filter((soldier) => isFieldedStargraveSoldier(soldier) && !soldier.isTemporary && !benchedSoldierIds.has(soldier.id))
            .map((soldier) => ({ id: soldier.id, name: soldier.name })),
        ])
      // Targets already at the buff's stack limit are not offered again.
      .filter((option) => !alreadyHasBuff(option.id, buff.id, buff.stacking ?? 1));
    return buff.id === 'armour-plates'
      ? options.filter((option) => !combatArmourWearerIds.has(option.id))
      : options;
  };
  const resolvedPowerBuffs = powers.flatMap(({ key, leader, power, selection }) => {
    const buff = getStargraveBuffForPower(power.id);
    if (!buff || !selectedPowers.includes(key)) return [];
    const options = buffTargetOptions(power, leader, buff);
    const memberId = powerBuffTargets[key] && options.some((option) => option.id === powerBuffTargets[key])
      ? powerBuffTargets[key]
      : options[0]?.id;
    if (!memberId) return [];
    return [{ buffId: buff.id, memberId, strainDamage: selection.strain > 0 ? selection.strain : undefined }];
  });
  // Robots eligible for Re-wire: not already re-wired and not picked by another resolved use.
  const rewireCandidates = (currentKey: string) => crewData.soldiers.filter((soldier) =>
    soldier.isRobot && !soldier.rewiredStat && !soldier.isTemporary &&
    !Object.entries(rewireSelections).some(([key, selection]) =>
      key !== currentKey && selectedPowers.includes(key) && selection.soldierId === soldier.id));
  const resolvedRewires = powers.flatMap(({ key, power }) => {
    if (power.id !== 're-wire-robot' || !selectedPowers.includes(key)) return [];
    const selection = rewireSelections[key];
    return selection?.soldierId && rewireCandidates(key).some((robot) => robot.id === selection.soldierId)
      ? [{ soldierId: selection.soldierId, stat: selection.stat }]
      : [];
  });

  const hasMedic = crewData.soldiers.some((soldier) => soldier.profileId === 'medic');
  const injuredLeaders = leaders.flatMap((leader) => {
    const neverQuiteAsStrong = leader.permanentInjuryIds.filter((id) => id === 'never-quite-as-strong').length;
    const lingering = leader.permanentInjuryIds.filter((id) => id === 'lingering-injury').length;
    const badlyWounded = Boolean(leader.gameState.badlyWounded);
    if (neverQuiteAsStrong === 0 && lingering === 0 && !badlyWounded) return [];
    const treatmentCost = (lingering >= 2 ? 40 : 30) - (hasMedic ? 10 : 0);
    const healthPenalty = lingering >= 2 ? 4 : 3;
    const woundTreatmentCost = hasMedic ? 50 : 100;
    return [{ leader, neverQuiteAsStrong, lingering, badlyWounded, treatmentCost, healthPenalty, woundTreatmentCost }];
  });
  const totalTreatmentCost = injuredLeaders.reduce(
    (total, entry) => total
      + (entry.lingering > 0 && lingeringPayments[entry.leader.id] ? entry.treatmentCost : 0)
      + (entry.badlyWounded && badlyWoundedPayments[entry.leader.id] ? entry.woundTreatmentCost : 0), 0);
  const totalCost = totalTreatmentCost + totalUpkeepCost;

  return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box>
      <Typography variant="h5">Pre-Game Setup</Typography>
      <Typography color="text.secondary">
        Resolve any Out of Game Powers before entering the battle.
      </Typography>
    </Box>

    {powers.length === 0 ? (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography color="text.secondary">
          No Out of Game (B) Powers are available for this crew.
        </Typography>
      </Paper>
    ) : (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Out of Game (B) Powers</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select each Power resolved before the game. Its rule effect should be applied to the crew as appropriate.
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
          {powers.map(({ key, leader, power, selection }) => {
            const selected = selectedPowers.includes(key);
            const buff = getStargraveBuffForPower(power.id);
            const targetOptions = buff ? buffTargetOptions(power, leader, buff) : [];
            const targetId = powerBuffTargets[key] && targetOptions.some((option) => option.id === powerBuffTargets[key])
              ? powerBuffTargets[key]
              : targetOptions[0]?.id ?? '';
            return (
              <Box key={key} sx={{ display: 'flex', flexDirection: 'column', '& > *': { flex: 1 }, borderRadius: 1, outline: selected ? '2px solid' : 'none', outlineColor: 'primary.main' }}>
                <PowerCard
                  name={`${power.name} — ${leader.name}`}
                  activation={selection.activation}
                  strain={selection.strain}
                  timing={power.timing}
                  summary={power.summary}
                  hints={getShipUpgradePowerHints(crewData, power.id)}
                  onClick={() => setSelectedPowers((previous) =>
                    selected ? previous.filter((candidate) => candidate !== key) : [...previous, key])}
                />
                {selected && buff && (
                  <Box sx={{ flex: '0 0 auto', mt: 1 }}>
                    <FormControl size="small" fullWidth disabled={targetOptions.length === 0}>
                      <InputLabel id={`power-buff-${key}`}>Apply {buff.name} to</InputLabel>
                      <Select labelId={`power-buff-${key}`} label={`Apply ${buff.name} to`} value={targetId}
                        onChange={(event) => setPowerBuffTargets((previous) => ({ ...previous, [key]: String(event.target.value) }))}>
                        {targetOptions.map((option) => <MenuItem key={option.id} value={option.id}>{option.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    {targetOptions.length === 0 && <Typography variant="caption" color="text.secondary">
                      No valid target — the buff will not be applied.
                    </Typography>}
                    {selection.strain > 0 && targetOptions.length > 0 && <Typography variant="caption" color="text.secondary">
                      Starts the game at -{selection.strain} Health (Strain).
                    </Typography>}
                  </Box>
                )}
                {selected && power.id === 're-wire-robot' && (() => {
                  const candidates = rewireCandidates(key);
                  const rewire = rewireSelections[key];
                  return <Box sx={{ flex: '0 0 auto', mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControl size="small" fullWidth disabled={candidates.length === 0}>
                      <InputLabel id={`rewire-${key}`}>Re-wire robot</InputLabel>
                      <Select labelId={`rewire-${key}`} label="Re-wire robot"
                        value={rewire?.soldierId && candidates.some((robot) => robot.id === rewire.soldierId) ? rewire.soldierId : ''}
                        onChange={(event) => setRewireSelections((previous) => ({
                          ...previous,
                          [key]: { soldierId: String(event.target.value), stat: previous[key]?.stat ?? 'move' },
                        }))}>
                        <MenuItem value="">None</MenuItem>
                        {candidates.map((robot) => <MenuItem key={robot.id} value={robot.id}>{robot.name}</MenuItem>)}
                      </Select>
                    </FormControl>
                    {candidates.length === 0 && <Typography variant="caption" color="text.secondary">
                      No robots available — every robot is already re-wired or none are in the crew.
                    </Typography>}
                    {rewire?.soldierId && candidates.some((robot) => robot.id === rewire.soldierId) && (
                      <ToggleButtonGroup size="small" exclusive value={rewire.stat}
                        onChange={(_, value) => value && setRewireSelections((previous) => ({
                          ...previous,
                          [key]: { soldierId: rewire.soldierId, stat: value },
                        }))}
                        aria-label="Re-wire enhancement">
                        <ToggleButton value="move">+1 Move</ToggleButton>
                        <ToggleButton value="fight">+1 Fight</ToggleButton>
                        <ToggleButton value="armour">+1 Armour</ToggleButton>
                      </ToggleButtonGroup>
                    )}
                  </Box>;
                })()}
              </Box>
            );
          })}
        </Box>
      </Paper>
    )}

    {injuredLeaders.length > 0 && <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Injuries &amp; Recovery</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These effects are applied when the game starts.{hasMedic && ' Treatment costs include the medic\u2019s discount.'}
      </Typography>
      <Stack spacing={2}>
        {injuredLeaders.map(({ leader, neverQuiteAsStrong, lingering, badlyWounded, treatmentCost, healthPenalty, woundTreatmentCost }) => (
          <Box key={leader.id}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{leader.name}</Typography>
            {badlyWounded && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body2" sx={{ mb: 0.75 }}>
                  Badly Wounded: pay for treatment or start at half Health.
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={badlyWoundedPayments[leader.id] ? 'pay' : 'penalty'}
                  onChange={(_, value) => value && setBadlyWoundedPayments((previous) => ({ ...previous, [leader.id]: value === 'pay' }))}
                  aria-label={`${leader.name} badly wounded`}
                >
                  <ToggleButton value="pay" disabled={!badlyWoundedPayments[leader.id] && totalCost + woundTreatmentCost > availableCredits}>
                    Pay {woundTreatmentCost}cr
                  </ToggleButton>
                  <ToggleButton value="penalty">Start at half Health</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            {neverQuiteAsStrong > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Never Quite As Strong{neverQuiteAsStrong > 1 ? ` \u00d7${neverQuiteAsStrong}` : ''}: starts the game at -{neverQuiteAsStrong} Health (applied automatically).
              </Typography>
            )}
            {lingering > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="body2" sx={{ mb: 0.75 }}>
                  Lingering Injury{lingering > 1 ? ` \u00d7${lingering}` : ''}: pay for treatments or start at -{healthPenalty} Health.
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={lingeringPayments[leader.id] ? 'pay' : 'penalty'}
                  onChange={(_, value) => value && setLingeringPayments((previous) => ({ ...previous, [leader.id]: value === 'pay' }))}
                  aria-label={`${leader.name} lingering injury`}
                >
                  <ToggleButton value="pay" disabled={!lingeringPayments[leader.id] && totalCost + treatmentCost > availableCredits}>
                    Pay {treatmentCost}cr
                  </ToggleButton>
                  <ToggleButton value="penalty">Start at -{healthPenalty} Health</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
          </Box>
        ))}
      </Stack>
      {totalTreatmentCost > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Total treatment cost: {totalTreatmentCost}cr ({availableCredits}cr available).
        </Typography>
      )}
    </Paper>}

    {upkeepSuits.length > 0 && <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Combat Armour Upkeep</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each fielded suit of combat armour must be paid for before the game.
        {waiverLimit > 1 ? ' The Armament Workshop allows two Armoury waivers.' : ''}
      </Typography>
      <Stack spacing={2}>
        {upkeepSuits.map((suit) => {
          const decision = effectiveDecisions[suit.key];
          const replacement = upkeepReplacements[suit.ownerId] ?? 'none';
          const isSoldierSuit = suit.key.startsWith('soldier:');
          return (
            <Box key={suit.key}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {suit.ownerName} — {suit.itemName}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                value={decision}
                onChange={(_, value) => value && setUpkeepDecisions((previous) => ({ ...previous, [suit.key]: value }))}
                sx={{ mt: 0.5, flexWrap: 'wrap' }}
                aria-label={`${suit.ownerName} ${suit.itemName} upkeep`}
              >
                <ToggleButton value="pay" disabled={decision !== 'pay' && totalCost + suit.cost > availableCredits}>
                  Pay {suit.cost}cr
                </ToggleButton>
                <ToggleButton value="waived" disabled={!armouryResolved || (decision !== 'waived' && armouryUsesSpent >= waiverLimit)}>
                  Waived (Armoury)
                </ToggleButton>
                <ToggleButton value="free">Field for free</ToggleButton>
                <ToggleButton value="unpaid">Don't pay</ToggleButton>
              </ToggleButtonGroup>
              {decision === 'unpaid' && suit.key.startsWith('gear:') && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  The suit is left behind this game; exchange it for other gear in manage mode.
                </Typography>
              )}
              {decision === 'unpaid' && suit.key.startsWith('tech:') && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  The suit is returned to the ship's cargo.
                </Typography>
              )}
              {decision === 'unpaid' && isSoldierSuit && (
                <Box sx={{ mt: 0.75 }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    {suit.ownerName} sits this game out. Field a temporary replacement?
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={replacement}
                    onChange={(_, value) => value && setUpkeepReplacements((previous) => ({ ...previous, [suit.ownerId]: value }))}
                    aria-label={`${suit.ownerName} temporary replacement`}
                  >
                    <ToggleButton value="none">No replacement</ToggleButton>
                    <ToggleButton value="recruit">Temporary Recruit</ToggleButton>
                    <ToggleButton value="runner">Temporary Runner</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Total upkeep: {totalUpkeepCost}cr{totalTreatmentCost > 0 ? ` — combined with treatments: ${totalCost}cr` : ''} ({availableCredits}cr available).
      </Typography>
    </Paper>}

    {hasArmouryPower && <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Armoury Benefit</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each resolved Armoury use covers one upkeep waiver or gives one standard pistol, carbine, or shotgun +1 Damage for this game
        ({armouryUsesSpent}/{waiverLimit} used{armouryResolved ? '' : ' — mark the Armoury power as resolved first'}).
      </Typography>
      {weaponBoosts.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          {weaponBoosts.map((boost) => {
            const option = boostOptions.find((candidate) =>
              candidate.memberId === boost.memberId && candidate.weaponId === boost.weaponId);
            return <Chip
              key={`${boost.memberId}|${boost.weaponId}`}
              label={`${option?.memberName ?? boost.memberId} — ${option?.weaponName ?? boost.weaponId} +1 Damage`}
              onDelete={() => setWeaponBoosts((previous) =>
                previous.filter((candidate) => candidate !== boost))}
            />;
          })}
        </Stack>
      )}
      <FormControl size="small" sx={{ minWidth: 260 }}
        disabled={!armouryResolved || armouryUsesSpent >= waiverLimit || boostOptions.length === 0}>
        <InputLabel id="armoury-boost-label">Add weapon boost</InputLabel>
        <Select
          labelId="armoury-boost-label"
          label="Add weapon boost"
          value=""
          onChange={(event) => {
            const [memberId, weaponId] = String(event.target.value).split('|');
            setWeaponBoosts((previous) => [...previous, { memberId, weaponId }]);
          }}
        >
          {boostOptions
            .filter((option) => !weaponBoosts.some((boost) => boost.memberId === option.memberId))
            .map((option) => (
              <MenuItem key={`${option.memberId}|${option.weaponId}`} value={`${option.memberId}|${option.weaponId}`}>
                {option.memberName} — {option.weaponName}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      {boostOptions.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          No fielded crew member carries a standard pistol, carbine, or shotgun.
        </Typography>
      )}
    </Paper>}

    {hasWeaponLocker && <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>Weapon Locker</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        One soldier may carry a +1 Damage pistol, carbine, or shotgun this game; it occupies a gear slot.
      </Typography>
      <FormControl size="small" sx={{ minWidth: 260 }} disabled={weaponLockerCandidates.length === 0}>
        <InputLabel id="weapon-locker-label">Soldier</InputLabel>
        <Select
          labelId="weapon-locker-label"
          label="Soldier"
          value={weaponLockerSoldierId}
          onChange={(event) => setWeaponLockerSoldierId(String(event.target.value))}
        >
          <MenuItem value="">None</MenuItem>
          {weaponLockerCandidates.map((soldier) => (
            <MenuItem key={soldier.id} value={soldier.id}>{soldier.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
    </Paper>}

    {leaders.length > 0 && (() => {
      const temporarySoldiers = crewData.soldiers.filter((soldier) => soldier.isTemporary);
      return <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Temporary Soldiers</Typography>
          <IconButton size="small" aria-label="Add temporary soldier" onClick={onAddTemporarySoldier}>
            <AddIcon />
          </IconButton>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Free for one game; removed automatically after the game.
        </Typography>
        {temporarySoldiers.length === 0 ? (
          <Typography color="text.secondary">No temporary soldiers.</Typography>
        ) : (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
            {temporarySoldiers.map((soldier) => {
              const profile = getAllStargraveSoldiers().find((candidate) => candidate.id === soldier.profileId);
              return <Chip
                key={soldier.id}
                label={`${soldier.name} | ${profile?.name ?? 'Soldier'}`}
                onDelete={() => onRemoveSoldier(soldier.id)}
              />;
            })}
          </Stack>
        )}
      </Paper>;
    })()}

    {leaders.length > 0 && (() => {
      const fieldedSoldiers = crewData.soldiers
        .filter((soldier) => isFieldedStargraveSoldier(soldier) && !benchedSoldierIds.has(soldier.id))
        .map((soldier) => ({
          soldier,
          profile: getAllStargraveSoldiers().find((candidate) => candidate.id === soldier.profileId),
        }));
      const specialistLimit = 4 + (crewData.ship.upgradeIds.includes('extra-quarters') ? 1 : 0);
      const fieldedSpecialists = fieldedSoldiers.filter(({ profile }) => profile?.category === 'specialist').length;
      return <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Crew Ready</Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {leaders.map((leader) => {
            const background = stargraveBackgrounds.find((candidate) => candidate.id === leader.backgroundId);
            return <Chip key={leader.id} label={`${leader.name} | ${background?.name ?? 'Leader'}`} />;
          })}
          {fieldedSoldiers.map(({ soldier, profile }) => (
            <Chip key={soldier.id} label={`${soldier.name} | ${profile?.name ?? 'Soldier'}`} />
          ))}
        </Stack>
        {fieldedSoldiers.length > 8 && <Alert severity="warning" sx={{ mt: 1.5 }}>
          Fielding {fieldedSoldiers.length} soldiers, above the normal limit of 8.
        </Alert>}
        {fieldedSpecialists > specialistLimit && <Alert severity="warning" sx={{ mt: 1.5 }}>
          Fielding {fieldedSpecialists} specialists, above the limit of {specialistLimit}.
        </Alert>}
        {fieldedSoldiers.length < 8 && <Alert severity="info" sx={{ mt: 1.5 }}>
          Fielding {fieldedSoldiers.length} of 8 possible soldiers.
        </Alert>}
      </Paper>;
    })()}

    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
      <Button
        variant="contained"
        startIcon={<PlayArrowIcon />}
        disabled={totalCost > availableCredits}
        onClick={() => onStartGame({
          lingeringPayments,
          badlyWoundedPayments,
          powerBuffs: resolvedPowerBuffs,
          robotRewires: resolvedRewires,
          weaponLocker: weaponLockerSoldierId && weaponLockerCandidates.some((soldier) => soldier.id === weaponLockerSoldierId)
            ? { soldierId: weaponLockerSoldierId }
            : undefined,
          armourUpkeep: {
            decisions: effectiveDecisions,
            weaponBoosts: effectiveBoosts.filter((boost) => boostOptions.some((option) =>
              option.memberId === boost.memberId && option.weaponId === boost.weaponId)),
            replacements: Object.fromEntries(
              upkeepSuits
                .filter((suit) => suit.key.startsWith('soldier:') && effectiveDecisions[suit.key] === 'unpaid')
                .flatMap((suit) => {
                  const replacement = upkeepReplacements[suit.ownerId];
                  return replacement && replacement !== 'none' ? [[suit.ownerId, replacement]] : [];
                })
            ),
          },
        })}
      >
        Start the game
      </Button>
    </Box>
  </Box>;
};
